import { cn } from '@/lib/utils';
import { useEffect, useMemo, useRef } from 'react';

import {
  type IAction,
  type IStep,
  useChatData,
  useChatMessages
} from '@chainlit/react-client';

import { RunMark } from '@/components/runs/RunMark';
import { Button } from '@/components/ui/button';

/**
 * The question the assistant is waiting on, parked directly above the input.
 *
 * The first person to use ask_choice never noticed the assistant had stopped
 * for him: he kept reading, the window ran out, and he got the default reading
 * instead of the one he meant. The buttons were there — in the message stream,
 * which is where you read, not where you act. When you finish reading you go to
 * the composer to type, and the composer looked entirely normal.
 *
 * So the question comes to the composer instead. Not a modal: a modal would
 * cover the answer you need in order to choose, trap you if you meant to say
 * something else entirely, and there is no modal primitive on mobile at all.
 * This sits in the one place you cannot avoid looking at before typing, and
 * leaves the rest of the page readable.
 *
 * The question sits here too, directly over its own choices. Leaving it up in
 * the thread put the question at the top of the screen and the buttons at the
 * bottom, which reads as two unrelated things. While the ask is pending the
 * message that carries it renders nothing (Messages/Message/index.tsx), so the
 * question still appears exactly once — here. Once answered, that message comes
 * back carrying the question and the chosen reading, as the record.
 */

interface Props {
  className?: string;
}

export default function PendingAskBar({ className }: Props) {
  const { askUser, actions } = useChatData();
  const { messages } = useChatMessages();
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  const isActionAsk = askUser?.spec.type === 'action';
  const choices: IAction[] = isActionAsk
    ? actions.filter((a) => askUser?.spec.keys?.includes(a.id))
    : [];

  const question = useMemo(() => {
    const stepId = askUser?.spec.step_id;
    if (!stepId) return null;
    const find = (steps: IStep[]): IStep | undefined => {
      for (const step of steps) {
        if (step.id === stepId) return step;
        const nested = step.steps ? find(step.steps) : undefined;
        if (nested) return nested;
      }
      return undefined;
    };
    return find(messages)?.output?.trim() || null;
  }, [messages, askUser]);

  // Reach for the choice without reaching for the mouse: focus lands on the
  // first option, and 1..4 pick directly.
  useEffect(() => {
    if (!choices.length) return;
    firstButtonRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      // Never steal a digit the user is typing into the composer.
      if (
        target &&
        (target.tagName === 'TEXTAREA' ||
          target.tagName === 'INPUT' ||
          target.isContentEditable)
      ) {
        return;
      }
      const index = Number(e.key) - 1;
      if (Number.isInteger(index) && index >= 0 && index < choices.length) {
        e.preventDefault();
        askUser?.callback(choices[index]);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [choices.length, askUser]);

  if (!isActionAsk || !choices.length) return null;

  return (
    <div
      id="pending-ask-bar"
      role="group"
      className={cn(
        'absolute left-0 right-0 bottom-full z-40 mb-3',
        // Orange is "waiting for your choice" everywhere: here and on the
        // conversation in the sidebar.
        'animate-slide-up rounded-xl border border-orange-300 bg-orange-50',
        'dark:border-orange-800/70 dark:bg-orange-950/40',
        'p-3 shadow-lg',
        className
      )}
    >
      <div className="mb-2 flex items-center gap-2 text-[12.5px] font-semibold text-orange-600 dark:text-orange-400">
        <RunMark state="needs_choice" />
        Waiting for your choice
      </div>
      {question ? (
        <p className="mb-3 text-sm font-medium leading-snug text-foreground">
          {question}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {choices.map((action, i) => {
          const label = action.label || action.name;
          const hint =
            action.tooltip && action.tooltip !== label ? action.tooltip : null;
          return (
            <Button
              key={action.id}
              ref={i === 0 ? firstButtonRef : undefined}
              variant="outline"
              onClick={() => askUser?.callback(action)}
              className={cn(
                'h-auto min-h-11 flex-col items-start gap-0.5',
                'whitespace-normal break-words px-4 py-2.5 text-start',
                'border-orange-300 bg-background text-orange-800 hover:border-orange-500 hover:bg-orange-100',
                'dark:border-orange-800 dark:text-orange-200 dark:hover:bg-orange-900/40'
              )}
            >
              <span className="font-medium">{label}</span>
              {hint ? (
                <span className="text-xs font-normal text-muted-foreground">
                  {hint}
                </span>
              ) : null}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
